/**
 * A layer's zoom levels, set from its menu (production's "Zoom levels…", Rahman 28 Oct 2026: "How about a full zoom
 * level adjustability. So a range slider and maybe the modal has an always on / default toggle").
 *
 * The slider runs over production's round scales, furthest out first: its left handle is the furthest out the layer
 * draws, its right handle the closest in, and either end is no limit. The map draws a layer only where its tiles are.
 * The publish tiles every layer from zoom 0 (an overview below the layer's own minimum), and the manifest says where a
 * version's tiles start; one tiled before that starts at the layer's own minimum. The left handle stops where the
 * tiles start, so with tiles from zoom 0 it reaches no limit out and Always on is every zoom level.
 *
 * Ranges are map zooms (the engine's minzoom and maxzoom), remembered per project in this browser. Pure: tested in Node.
 */
import { scaleForZoom, zoomForScale } from './symbology';

/** The slider's stops, furthest out first. Position 0 is no limit out, position STOPS.length + 1 no limit in. */
export const STOPS = [5000000, 2500000, 1000000, 500000, 250000, 100000, 50000, 25000, 10000, 5000, 2500, 1000, 500];
export const LAST = STOPS.length + 1;

/** The scale for a slider position: the left handle's (0 for no limit out) and the right handle's (0 for no limit in). */
export const minAt = (i: number): number => (i <= 0 || i > STOPS.length ? 0 : (STOPS[i - 1] as number));
export const maxAt = (i: number): number => (i >= LAST || i < 1 ? 0 : (STOPS[i - 1] as number));

/** Where a scale sits on the slider, between stops, by its logarithm. */
export function posOf(scale: number): number {
  if (!(scale > 0)) return 0;
  const first = STOPS[0] as number;
  const last = STOPS[STOPS.length - 1] as number;
  if (scale >= first) return 1;
  if (scale <= last) return STOPS.length;
  const lg = Math.log(scale);
  for (let i = 0; i < STOPS.length - 1; i++) {
    const a = Math.log(STOPS[i] as number);
    const b = Math.log(STOPS[i + 1] as number);
    if (lg <= a && lg >= b) return i + 1 + (a - lg) / (a - b);
  }
  return STOPS.length;
}

/** A layer's zoom range in map zooms; no max is no limit in. */
export interface ZoomRange {
  min: number;
  max?: number;
}

/** A max this close in or closer is no limit in (configs say 24, the engine's deepest zoom). */
export const NO_LIMIT_IN = 22;

/** The range's limit in, or undefined for none. */
const limitIn = (r: ZoomRange): number | undefined => (r.max !== undefined && r.max < NO_LIMIT_IN ? r.max : undefined);

/** The engine's deepest zoom: a style layer's limit in when its layer has none. */
const DEEPEST = 24;

/** The zoom the layer's tiles start at (the manifest's tiles_minzoom, else its own minimum): nothing draws further out. */
export const dataStart = (own: ZoomRange, tilesFrom?: number): number => Math.max(0, Math.floor(tilesFrom ?? own.min));

/** The furthest-out position the left handle can take: the first stop not further out than the data's start. */
export function firstPos(start: number, latitude: number): number {
  if (start <= 0) return 0;
  const scale = scaleForZoom(start, latitude);
  const i = STOPS.findIndex((s) => s <= scale);
  return i < 0 ? STOPS.length : i + 1;
}

const near = (a: number, b: number): boolean => Math.abs(a - b) < 0.01;

/** The handles for a range: the nearest stops, the ends for no limit, the left one no further out than the data. */
export function handlesOf(range: ZoomRange, start: number, latitude: number): [number, number] {
  const first = firstPos(start, latitude);
  const lo = range.min <= start + 0.01 ? first : Math.max(first, Math.round(posOf(scaleForZoom(range.min, latitude))));
  const max = limitIn(range);
  let hi = max === undefined ? LAST : Math.round(posOf(scaleForZoom(max, latitude)));
  if (hi <= lo) hi = Math.min(LAST, lo + 1);
  return [Math.min(lo, LAST - 1), hi];
}

/** The range for a pair of handles. The left handle at its furthest out is the data's start. */
export function rangeAt(lo: number, hi: number, start: number, latitude: number): ZoomRange {
  const min = lo <= firstPos(start, latitude) ? start : Math.max(start, zoomForScale(minAt(lo), latitude));
  const round = (z: number): number => Math.round(z * 100) / 100;
  return hi >= LAST ? { min: round(min) } : { min: round(min), max: round(zoomForScale(maxAt(hi), latitude)) };
}

/** The range is the layer's own. */
export function isDefault(range: ZoomRange, own: ZoomRange): boolean {
  const a = limitIn(range);
  const b = limitIn(own);
  return near(range.min, own.min) && (a === undefined ? b === undefined : b !== undefined && near(a, b));
}

/** The range is as wide as the layer's data allows: from its start out to no limit in. */
export const isAlwaysOn = (range: ZoomRange, start: number): boolean =>
  range.min <= start + 0.01 && limitIn(range) === undefined;

/** The widest range the layer's data allows. */
export const alwaysOn = (start: number): ZoomRange => ({ min: start });

/** A range no further out than where a layer's tiles start (a group's range, set on each layer in it). */
export const withinData = (range: ZoomRange, start: number): ZoomRange =>
  range.min >= start ? range : { ...range, min: start };

/** The quiet note at the slider's stop: the scale a layer's data starts at, as 1:72k or 1:1.2M ('' from zoom 0). */
export function dataFrom(start: number, latitude: number): string {
  if (start <= 0) return '';
  const scale = scaleForZoom(start, latitude);
  const short = scale >= 1e6 ? `${Math.round(scale / 1e5) / 10}M` : `${Math.round(scale / 1000)}k`;
  return `Data from 1:${short}`;
}

/**
 * The layer's style for a range. Its style layers start and end where the layer does, so those limits follow the range:
 * a wider range draws further out (or in) than the layer's own. A label that starts closer in than the layer keeps it.
 */
export function styleForRange<T extends Record<string, unknown>>(
  style: readonly T[],
  own: ZoomRange,
  range: ZoomRange,
): T[] {
  const ownIn = own.max ?? DEEPEST;
  return style.map((l) => {
    const min = l['minzoom'];
    const max = l['maxzoom'];
    return {
      ...l,
      ...(typeof min === 'number' && min <= own.min ? { minzoom: range.min } : {}),
      ...(typeof max === 'number' && max >= ownIn ? { maxzoom: range.max ?? DEEPEST } : {}),
    };
  });
}

/* ── What each end looks like (production's pictures, scale and ground width) ── */

export type Picture = 'close' | 'building' | 'streets' | 'town' | 'region' | 'state' | 'world';

export const PICTURE_NAMES: Record<Picture, string> = {
  close: 'Up close',
  building: 'A building',
  streets: 'Streets',
  town: 'A town',
  region: 'A region',
  state: 'A state',
  world: 'The world',
};

/** What fills the map at a scale; no limit is the world going out and up close going in. */
export function pictureFor(scale: number, out: boolean): Picture {
  if (!scale) return out ? 'world' : 'close';
  if (scale <= 1000) return 'building';
  if (scale <= 5000) return 'streets';
  if (scale <= 25000) return 'town';
  if (scale <= 250000) return 'region';
  if (scale <= 2500000) return 'state';
  return 'world';
}

/** The ground a map this many pixels wide covers at 1:scale, at 96 dots to the inch. */
export function across(scale: number, widthPx: number): string {
  if (!widthPx || !scale) return '';
  const m = (widthPx / (96 / 0.0254)) * scale;
  if (m < 1000) return `${Math.round(m / 10) * 10} m across`;
  if (m < 10000) return `${(m / 1000).toFixed(1)} km across`;
  return `${Math.round(m / 1000).toLocaleString()} km across`;
}

/* ── Remembered per project in this browser ── */

export const ZOOM_KEY = 'mm_layer_zoom_v1';

type Kept = Record<string, Record<string, [number, number | null]>>;

function readKept(storage: Pick<Storage, 'getItem'> | null): Kept {
  try {
    const raw: unknown = JSON.parse(storage?.getItem(ZOOM_KEY) ?? '{}');
    return raw && typeof raw === 'object' ? (raw as Kept) : {};
  } catch {
    return {};
  }
}

/** The ranges this browser remembers for a project, by layer id. */
export function loadZoomRanges(storage: Pick<Storage, 'getItem'> | null, scope: string): Map<string, ZoomRange> {
  const out = new Map<string, ZoomRange>();
  for (const [id, r] of Object.entries(readKept(storage)[scope] ?? {})) {
    if (!Array.isArray(r) || typeof r[0] !== 'number' || !Number.isFinite(r[0])) continue;
    const max = r[1];
    out.set(id, typeof max === 'number' && Number.isFinite(max) ? { min: r[0], max } : { min: r[0] });
  }
  return out;
}

/** Remember a project's ranges (an empty set forgets the project). */
export function saveZoomRanges(
  storage: Pick<Storage, 'getItem' | 'setItem'> | null,
  scope: string,
  ranges: ReadonlyMap<string, ZoomRange>,
): void {
  if (!storage || !scope) return;
  const all = readKept(storage);
  if (ranges.size) {
    all[scope] = Object.fromEntries(
      [...ranges].map(([id, r]) => [id, [r.min, r.max ?? null] as [number, number | null]]),
    );
  } else delete all[scope];
  try {
    storage.setItem(ZOOM_KEY, JSON.stringify(all));
  } catch {
    // A convenience: blocked storage just means the ranges last this visit only.
  }
}
